import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const WebhookConfiguration = ({ webhooks, onCreateWebhook, onUpdateWebhook, onDeleteWebhook }) => {
  const [isCreating, setIsCreating] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [newWebhook, setNewWebhook] = useState({
    name: '',
    url: '',
    events: [],
    secret: '',
    active: true
  });

  const eventOptions = [
    { value: 'agent.launched', label: 'Agent Launched', description: 'When an AI agent is deployed' },
    { value: 'data.processed', label: 'Data Processed', description: 'When data analysis completes' },
    { value: 'integration.connected', label: 'Integration Connected', description: 'When a new integration is established' },
    { value: 'integration.failed', label: 'Integration Failed', description: 'When an integration encounters an error' },
    { value: 'report.generated', label: 'Report Generated', description: 'When a new report is created' },
    { value: 'sync.completed', label: 'Sync Completed', description: 'When data synchronization finishes' }
  ];

  const handleCreateWebhook = () => {
    if (newWebhook?.name && newWebhook?.url && newWebhook?.events?.length > 0) {
      const webhook = {
        ...newWebhook,
        id: Date.now()?.toString(),
        createdAt: new Date()?.toISOString(),
        lastTriggered: null,
        deliveryCount: 0,
        status: 'active'
      };
      
      onCreateWebhook(webhook);
      setNewWebhook({
        name: '',
        url: '',
        events: [],
        secret: '',
        active: true
      });
      setIsCreating(false);
    }
  };

  const handleTestWebhook = async (webhook) => {
    // Simulate webhook test
    const testPayload = {
      event: 'webhook.test',
      timestamp: new Date()?.toISOString(),
      data: {
        message: 'This is a test webhook delivery from Ipsos Insight Intelligence Platform'
      }
    };

    console.log('Testing webhook:', webhook?.url, testPayload);
    // In real implementation, this would make an HTTP request
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-success';
      case 'failed':
        return 'text-destructive';
      case 'disabled':
        return 'text-muted-foreground';
      default:
        return 'text-muted-foreground';
    }
  };

  const formatLastTriggered = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date?.toLocaleDateString();
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-card-foreground">Webhook Configuration</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Configure webhooks for automated event notifications
          </p>
        </div>
        <Button
          variant="default"
          onClick={() => setIsCreating(true)}
          iconName="Plus"
          iconPosition="left"
        >
          Add Webhook
        </Button>
      </div>
      {/* Create/Edit Webhook Form */}
      {(isCreating || editingWebhook) && (
        <div className="bg-muted rounded-lg p-4 mb-6">
          <h3 className="font-medium text-card-foreground mb-4">
            {isCreating ? 'Create New Webhook' : 'Edit Webhook'}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Input
              label="Webhook Name"
              placeholder="e.g., Slack Notifications"
              value={newWebhook?.name}
              onChange={(e) => setNewWebhook(prev => ({ ...prev, name: e?.target?.value }))}
              required
            />
            
            <Input
              label="Endpoint URL"
              type="url"
              placeholder="https://your-app.com/webhooks"
              value={newWebhook?.url}
              onChange={(e) => setNewWebhook(prev => ({ ...prev, url: e?.target?.value }))}
              required
            />
          </div>

          <div className="mb-4">
            <Select
              label="Events to Subscribe"
              placeholder="Select events that will trigger this webhook"
              options={eventOptions}
              value={newWebhook?.events}
              onChange={(events) => setNewWebhook(prev => ({ ...prev, events }))}
              multiple
              searchable
              required
            />
          </div>

          <div className="mb-4">
            <Input
              label="Secret Key (Optional)"
              placeholder="Webhook signature secret"
              value={newWebhook?.secret}
              onChange={(e) => setNewWebhook(prev => ({ ...prev, secret: e?.target?.value }))}
              description="Used to verify webhook authenticity"
            />
          </div>

          <div className="flex items-center justify-between">
            <Checkbox
              label="Active"
              checked={newWebhook?.active}
              onChange={(e) => setNewWebhook(prev => ({ ...prev, active: e?.target?.checked }))}
            />
            
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setIsCreating(false);
                  setEditingWebhook(null);
                  setNewWebhook({
                    name: '',
                    url: '',
                    events: [],
                    secret: '',
                    active: true
                  });
                }}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleCreateWebhook}
                disabled={!newWebhook?.name || !newWebhook?.url || newWebhook?.events?.length === 0}
              >
                {isCreating ? 'Create Webhook' : 'Update Webhook'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Webhooks List */}
      <div className="space-y-4">
        {webhooks?.map((webhook) => (
          <div
            key={webhook?.id}
            className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors duration-200"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h4 className="font-medium text-card-foreground">{webhook?.name}</h4>
                  <span className={`text-xs px-2 py-1 rounded-full bg-muted ${getStatusColor(webhook?.status)}`}>
                    {webhook?.status}
                  </span>
                  {!webhook?.active && (
                    <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                      Disabled
                    </span>
                  )}
                </div>
                
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="Link" size={14} className="text-muted-foreground" />
                  <span className="text-sm font-mono text-muted-foreground break-all">
                    {webhook?.url}
                  </span>
                </div>

                <div className="flex flex-wrap gap-1 mb-3">
                  {webhook?.events?.map((event) => (
                    <span
                      key={event}
                      className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full"
                    >
                      {eventOptions?.find(opt => opt?.value === event)?.label || event}
                    </span>
                  ))}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Created:</span>
                    <p className="font-medium text-card-foreground">
                      {new Date(webhook.createdAt)?.toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Last Triggered:</span>
                    <p className="font-medium text-card-foreground">
                      {formatLastTriggered(webhook?.lastTriggered)}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Deliveries:</span>
                    <p className="font-medium text-card-foreground">
                      {webhook?.deliveryCount?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Success Rate:</span>
                    <p className="font-medium text-card-foreground">
                      {webhook?.successRate || '100%'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2 ml-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleTestWebhook(webhook)}
                  iconName="Zap"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEditingWebhook(webhook?.id);
                    setNewWebhook(webhook);
                  }}
                  iconName="Edit"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDeleteWebhook(webhook?.id)}
                  iconName="Trash2"
                  className="text-destructive hover:text-destructive"
                />
              </div>
            </div>
          </div>
        ))}

        {webhooks?.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Webhook" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="font-medium text-card-foreground mb-2">No Webhooks Configured</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Set up webhooks to receive real-time notifications about events
            </p>
            <Button
              variant="outline"
              onClick={() => setIsCreating(true)}
              iconName="Plus"
              iconPosition="left"
            >
              Create Your First Webhook
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WebhookConfiguration;